import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import styles from "./DeductibleCard.module.css";
import { TabPanel, TabView } from "primereact/tabview";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import * as client from "../../../client";
import { useContext } from "react";
import { ThemeContext } from "../../../themes/Theme";
import { Button } from "primereact/button";
import { testDeductibleOrLimitFunction } from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { readableNameFromPeril } from "../general-input/util";

interface DeductibleCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

export const DeductibleCard: React.FC<DeductibleCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const extractedDeductibles =
    specialtyPropertyInfo.report_json.deductible_info?.extracted_deductibles ??
    {};
  const manualDeductibles =
    specialtyPropertyInfo.report_json.deductible_info?.manual_deductibles ?? {};
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const toast = useToast();

  return (
    <Card title="Deductible">
      <TabView>
        {Object.entries(extractedDeductibles).map(
          ([filename, extractedDeductiblesForFile]) => {
            return (
              <TabPanel header={filename} key={filename}>
                <div className={styles.verticalContainer}>
                  {Object.entries(extractedDeductiblesForFile).map(
                    ([peril, advancedDeductible]) => {
                      const readablePerilName = readableNameFromPeril(
                        peril as client.peril_type
                      );
                      return (
                        <div
                          className={styles.verticalContainerSmall}
                          key={peril}
                        >
                          <div className={styles.horizontalContainer}>
                            <span className={styles.header}>
                              {readablePerilName}
                            </span>
                            <Button
                              label="Test"
                              outlined
                              size="small"
                              style={{ padding: "4px 6px" }}
                              onClick={async () => {
                                try {
                                  const result =
                                    await testDeductibleOrLimitFunction(
                                      specialtyPropertyInfo.report_id,
                                      filename,
                                      peril as client.peril_type,
                                      "deductible"
                                    );
                                  toast.show({
                                    severity: "success",
                                    summary: "Deductible Function Success",
                                    detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                                  });
                                } catch {
                                  toast.show({
                                    severity: "error",
                                    summary: "Deductible Function Failure",
                                    detail: `Something went wrong when running the ${readablePerilName} function.`,
                                  });
                                }
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px" }}>
                            {advancedDeductible.short_deductible_description}
                          </span>
                          <SyntaxHighlighter
                            language="python"
                            style={isLightMode ? oneLight : oneDark}
                            showLineNumbers
                            customStyle={{
                              fontSize: "13px",
                              lineHeight: "1.5",
                            }}
                          >
                            {advancedDeductible.executable_python_function_str}
                          </SyntaxHighlighter>
                        </div>
                      );
                    }
                  )}
                </div>
              </TabPanel>
            );
          }
        )}
        {Object.keys(manualDeductibles).length > 0 && (
          <TabPanel header="Manual">
            <div className={styles.verticalContainer}>
              {Object.entries(manualDeductibles).map(
                ([peril, advancedDeductible]) => {
                  const readablePerilName = readableNameFromPeril(
                    peril as client.peril_type
                  );
                  const hasFunction =
                    advancedDeductible.executable_python_function_str !== "";
                  return (
                    <div className={styles.verticalContainerSmall} key={peril}>
                      <div className={styles.horizontalContainer}>
                        <span className={styles.header}>
                          {readablePerilName}
                        </span>
                        {hasFunction && (
                          <Button
                            label="Test"
                            outlined
                            size="small"
                            style={{ padding: "4px 6px" }}
                            onClick={async () => {
                              try {
                                const result =
                                  await testDeductibleOrLimitFunction(
                                    specialtyPropertyInfo.report_id,
                                    null,
                                    peril as client.peril_type,
                                    "deductible"
                                  );
                                toast.show({
                                  severity: "success",
                                  summary: "Deductible Function Success",
                                  detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                                });
                              } catch {
                                toast.show({
                                  severity: "error",
                                  summary: "Deductible Function Failure",
                                  detail: `Something went wrong when running the ${readablePerilName} function.`,
                                });
                              }
                            }}
                          />
                        )}
                      </div>
                      <span style={{ fontSize: "14px" }}>
                        {advancedDeductible.short_deductible_description}
                      </span>
                      {hasFunction ? (
                        <SyntaxHighlighter
                          language="python"
                          style={isLightMode ? oneLight : oneDark}
                          showLineNumbers
                          customStyle={{
                            fontSize: "13px",
                            lineHeight: "1.5",
                          }}
                        >
                          {advancedDeductible.executable_python_function_str}
                        </SyntaxHighlighter>
                      ) : (
                        <span
                          style={{
                            fontSize: "13px",
                            fontStyle: "italic",
                            color: "var(--text-color-secondary)",
                          }}
                        >
                          Function will be generated on next rerun.
                        </span>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </TabPanel>
        )}
      </TabView>
    </Card>
  );
};
